import { goOnline, goOffline, ref, onValue, off, DatabaseReference } from 'firebase/database';
import { database } from './core';
import logger from '../../utils/logger';

// Connection state monitoring
let isConnected = false;
let connectionRef: DatabaseReference | null = null;
let connectionListener: ((snapshot: any) => void) | null = null;

/**
 * Ensures the Firebase Database is online and ready for authenticated operations
 * This should be called after successful authentication to ensure proper permissions
 *
 * IMPROVED (C5 Fix): Now actually waits for connection if not already connected
 * - If already connected: returns immediately (0ms delay)
 * - If not connected: waits up to timeoutMs for connection
 * - Returns boolean indicating connection status
 *
 * @param timeoutMs - Maximum time to wait for connection (default 5000ms)
 * @returns true if connected, false if timeout reached
 */
export const ensureDatabaseOnline = async (timeoutMs: number = 5000): Promise<boolean> => {
  try {
    if (!database) {
      logger.warn('[Database] Database not initialized');
      return false;
    }

    // Call goOnline to ensure Firebase tries to connect
    goOnline(database);

    // Start monitoring if not already started
    if (!connectionRef) {
      startConnectionMonitoring();
    }

    // FAST PATH: If already connected, return immediately (no delay)
    if (isConnected) {
      return true;
    }

    // SLOW PATH: Wait for connection with timeout
    // This only happens when not connected (app just opened, network issue, etc.)
    const startTime = Date.now();

    return new Promise<boolean>((resolve) => {
      const checkConnection = () => {
        // Check if connected
        if (isConnected) {
          const waitTime = Date.now() - startTime;
          if (waitTime > 100) {
            logger.info(`[Database] Connected after ${waitTime}ms`);
          }
          resolve(true);
          return;
        }

        // Check if timeout reached
        if (Date.now() - startTime >= timeoutMs) {
          logger.warn(`[Database] Connection timeout after ${timeoutMs}ms - proceeding anyway`);
          // Return true to allow operation to proceed - Firebase may still work
          // The operation itself will fail if truly disconnected
          resolve(true);
          return;
        }

        // Check again in 100ms
        setTimeout(checkConnection, 100);
      };

      checkConnection();
    });
  } catch (error) {
    logger.error('Error ensuring database online state:', error);
    // Return true to allow operation to proceed - let Firebase handle the error
    return true;
  }
};

/**
 * Takes the Firebase Database offline
 * Can be used for testing or to conserve resources
 */
export const takeDatabaseOffline = (): void => {
  try {
    if (!database) {
      logger.warn('Database not initialized, cannot go offline');
      return;
    }

    logger.info('Taking Firebase database offline');
    stopConnectionMonitoring();
    goOffline(database);
  } catch (error) {
    logger.error('Error taking database offline:', error);
  }
};

/**
 * Checks if the database connection is ready for authenticated operations
 * This is particularly important after login to ensure permissions work
 */
export const verifyDatabaseConnection = async (): Promise<boolean> => {
  try {
    if (!database) {
      logger.error('Database not initialized');
      return false;
    }

    // Ensure we're online
    await ensureDatabaseOnline();
    
    // The database should now be ready for authenticated operations
    return true;
  } catch (error) {
    logger.error('Error verifying database connection:', error);
    return false;
  }
};

/**
 * Gets the current connection state
 */
export const getConnectionState = (): { isConnected: boolean; hasPermissions: boolean } => {
  return { 
    isConnected, 
    hasPermissions: isConnected && !!database 
  };
};

/**
 * Starts monitoring the Firebase connection state
 */
const startConnectionMonitoring = (): void => {
  try {
    if (!database || connectionRef) {
      return; // Already monitoring or no database
    }

    connectionRef = ref(database, '.info/connected');
    
    connectionListener = (snapshot) => {
      try {
        if (snapshot && typeof snapshot.val === 'function') {
          const connected = snapshot.val() === true;
          
          if (connected !== isConnected) {
            isConnected = connected;
            logger.info(`🔗 Firebase connection state changed: ${connected ? 'CONNECTED' : 'DISCONNECTED'}`);
            
            // Dispatch custom event for other parts of the app
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('firebase-connection-changed', { 
                detail: { isConnected: connected } 
              }));
            }
          }
        }
      } catch (error) {
        logger.error('Error in connection listener:', error);
      }
    };

    onValue(connectionRef, connectionListener, (error) => {
      logger.error('❌ Error monitoring connection state:', error);
      // On error, stop monitoring to prevent infinite loops
      stopConnectionMonitoring();
    });

    logger.info('🔗 Started Firebase connection monitoring');
  } catch (error) {
    logger.error('❌ Error starting connection monitoring:', error);
    // Reset connection state on error
    connectionRef = null;
    connectionListener = null;
  }
};

/**
 * Stops monitoring the Firebase connection state
 */
const stopConnectionMonitoring = (): void => {
  try {
    if (connectionRef && connectionListener) {
      off(connectionRef, 'value', connectionListener);
      connectionRef = null;
      connectionListener = null;
      logger.info('Stopped Firebase connection monitoring');
    }
  } catch (error) {
    logger.error('Error stopping connection monitoring:', error);
  }
};

/**
 * Checks if an error is a network-related error that might resolve with retry
 */
const isNetworkError = (error: any): boolean => {
  const message = (error?.message || '').toLowerCase();
  const code = (error?.code || '').toLowerCase();
  return (
    message.includes('network') ||
    message.includes('offline') ||
    message.includes('connection') ||
    message.includes('timeout') ||
    message.includes('unavailable') ||
    code === 'network_error' ||
    code === 'unavailable'
  );
};

/**
 * Checks if an error is a permission error that won't resolve with retry
 */
const isPermissionError = (error: any): boolean => {
  const message = (error?.message || '').toLowerCase();
  const code = (error?.code || '').toLowerCase();
  return (
    message.includes('permission') ||
    message.includes('permission_denied') ||
    code === 'permission_denied' ||
    code === 'permission-denied'
  );
};

/**
 * Checks if an error is an authentication error that won't resolve with retry
 */
const isAuthError = (error: any): boolean => {
  const message = (error?.message || '').toLowerCase();
  return (
    message.includes('not authenticated') ||
    message.includes('unauthenticated') ||
    message.includes('please log in')
  );
};

/**
 * Executes a write operation with smart retry logic and exponential backoff
 *
 * IMPROVED (C7 Fix):
 * - Increased retries from 1 to 3 for better network resilience
 * - Exponential backoff: 1s, 2s, 4s (total ~7s max wait)
 * - Fails IMMEDIATELY for permission/auth errors (no wasted retries)
 * - Only retries network errors that might resolve with time
 *
 * @param operation The write operation to perform
 * @param maxRetries Maximum number of retry attempts (default 3)
 * @param initialDelay Initial delay in ms, doubles each retry (default 1000ms)
 * @returns Promise that resolves when operation succeeds or fails after all retries
 */
export const executeWithRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> => {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();

      if (attempt > 0) {
        logger.info(`✅ Operation succeeded on retry attempt ${attempt}`);
      }

      return result;
    } catch (error: any) {
      lastError = error;

      // FAIL FAST: Don't retry errors that won't resolve with time
      if (isPermissionError(error)) {
        logger.error('❌ Permission denied - not retrying');
        throw error;
      }

      if (isAuthError(error)) {
        logger.error('❌ Authentication error - not retrying');
        throw error;
      }

      // Only retry network errors that might resolve
      if (!isNetworkError(error)) {
        logger.error('❌ Non-retryable error:', error?.message || error);
        throw error;
      }

      // Check if we have retries left
      if (attempt < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = initialDelay * Math.pow(2, attempt);
        logger.warn(`⚠️ Network error (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  logger.error(`❌ Operation failed after ${maxRetries + 1} attempts`);
  throw lastError;
};

/**
 * Waits for the database connection to be established
 * @param timeout Maximum time to wait in milliseconds
 * @returns Promise that resolves when connected or rejects on timeout
 */
export const waitForConnection = async (timeout: number = 10000): Promise<void> => {
  const state = getConnectionState();
  if (state.isConnected) {
    return;
  }
  
  return new Promise((resolve, reject) => {
    let timeoutId: NodeJS.Timeout;
    
    const handleConnection = (event: any) => {
      if (event.detail.isConnected) {
        clearTimeout(timeoutId);
        if (typeof window !== 'undefined') {
          window.removeEventListener('firebase-connection-changed', handleConnection);
        }
        resolve();
      }
    };
    
    timeoutId = setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('firebase-connection-changed', handleConnection);
      }
      logger.warn(`⚠️ Connection timeout after ${timeout}ms`);
      reject(new Error(`Connection timeout after ${timeout}ms`));
    }, timeout);
    
    if (typeof window !== 'undefined') {
      window.addEventListener('firebase-connection-changed', handleConnection);
    }
    
    // Check once more in case we're already connected
    const currentState = getConnectionState();
    if (currentState.isConnected) {
      clearTimeout(timeoutId);
      if (typeof window !== 'undefined') {
        window.removeEventListener('firebase-connection-changed', handleConnection);
      }
      resolve();
    }
  });
};